/**
 * Iter60.p68 — Leave tab on Employee Profile.
 *
 * Renders per-employee:
 *   • Balance card (all applicable leave types, live from
 *     /api/leave/balances/employee/{id}) — Entitled/Used/Available
 *   • Annual accrual detail (cycle bounds + accrued/used/carried/next reset)
 *   • Sick tier status (cumulative + current tier)
 *   • Breastfeeding period (female employees only)
 *   • Request history (from /api/leave/requests?employee_id=…)
 *   • "New Leave Request" CTA prefilled with the employee
 *
 * Officer role: read-only VIEW; write actions hidden.
 * Casual worker: shows a policy banner (endpoint 404 → banner).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Plus, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { TableSkeleton } from "./SkeletonHelpers";
import EmptyState from "./EmptyState";
import { EntitledCell, UsedCell } from "./LeaveBalanceCell";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../utils/date";

const LEAVE_TYPE_ORDER = [
    "annual", "sick", "marriage", "hajj_umrah", "maternity",
    "paternity", "breastfeeding", "personal", "compassionate",
    "permission_hourly", "unpaid",
];
const LEAVE_TYPE_LABEL = {
    annual: "Annual",
    sick: "Sick",
    marriage: "Marriage",
    hajj_umrah: "Hajj/Umrah",
    maternity: "Maternity",
    paternity: "Paternity",
    breastfeeding: "Breastfeeding",
    personal: "Personal",
    compassionate: "Compassionate",
    permission_hourly: "Permission (hourly)",
    unpaid: "Unpaid",
};

const STATUS_TONE = {
    pending_dept: "bg-amber-50 text-amber-700 border-amber-200",
    pending_hr: "bg-[color:var(--asl-navy-50)] text-[color:var(--asl-navy)] border-[color:var(--asl-navy-50)]",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    cancelled: "bg-slate-50 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
    pending_dept: "Pending Dept",
    pending_hr: "Pending HR",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
};

function StatusPill({ status }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${STATUS_TONE[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}
            data-testid={`leave-tab-status-${status}`}
        >
            {STATUS_LABEL[status] || status}
        </span>
    );
}

function SectionHeader({ children, testid }) {
    return (
        <h4
            className="text-[11px] uppercase tracking-wider font-bold asl-navy-text mb-2"
            data-testid={testid}
        >
            {children}
        </h4>
    );
}

export default function StaffLeaveTab({ employee, moduleCode, onNewLeaveClick }) {
    const { user } = useAuth();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [casual, setCasual] = useState(false);

    const canCreate = ["kaafi_system_admin", "kaafi_hr_manager", "asl_department_manager"]
        .includes(user?.role);

    const load = async () => {
        setLoading(true);
        setCasual(false);
        try {
            const [balRes, reqRes] = await Promise.allSettled([
                api.get(`/leave/balances/employee/${employee.id}`),
                api.get(`/leave/requests`, {
                    params: { employee_id: employee.id, page_size: 100 },
                }),
            ]);
            if (balRes.status === "fulfilled") {
                setPayload(balRes.value.data);
            } else if (balRes.reason?.response?.status === 404) {
                // Casual worker — not entitled to any leave.
                setCasual(true);
                setPayload(null);
            } else {
                toast.error(formatApiError(balRes.reason));
            }
            if (reqRes.status === "fulfilled") {
                setRequests(reqRes.value.data.items || []);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employee?.id) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee?.id]);

    const balancesByCode = useMemo(() => {
        const map = {};
        for (const b of payload?.balances || []) map[b.leave_type_code] = b;
        return map;
    }, [payload]);

    const annual = balancesByCode.annual;
    const sick = balancesByCode.sick;
    const brf = balancesByCode.breastfeeding;

    const isFemale = (employee.gender || "").toLowerCase() === "female";
    const brfPeriod = employee.breastfeeding_period;
    const brfActive = brfPeriod?.start_date && (!brfPeriod?.expires_on
        || brfPeriod.expires_on >= new Date().toISOString().slice(0, 10));

    if (loading) {
        return (
            <div className="asl-card p-6" data-testid="staff-leave-loading">
                <TableSkeleton rows={5} cols={4} />
            </div>
        );
    }

    if (casual) {
        return (
            <div className="asl-card p-6 space-y-4" data-testid="staff-leave-casual">
                <div
                    className="border-l-4 asl-crimson-border rounded p-4"
                    style={{
                        background: "rgba(168,50,74,0.05)",
                        borderColor: "var(--asl-crimson)",
                    }}
                >
                    <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 asl-crimson-text mt-0.5" />
                        <div>
                            <div className="font-bold asl-navy-text text-sm">
                                Casual worker — no leave entitlement
                            </div>
                            <div className="text-xs text-[color:var(--asl-muted)] mt-1 max-w-2xl">
                                Per company policy, Casual workers are not entitled to
                                statutory leave (Annual, Sick, Maternity, etc.). Contact HR
                                to update the employee's category if this is expected to
                                change.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="staff-leave-tab">
            {/* -------- Balances card -------- */}
            <div className="asl-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <SectionHeader testid="staff-leave-balances-title">
                        Balances — {payload?.year || new Date().getFullYear()}
                    </SectionHeader>
                    {canCreate && (
                        <Button
                            size="sm"
                            className="asl-navy-bg text-white"
                            onClick={() => onNewLeaveClick && onNewLeaveClick(employee.id)}
                            data-testid="staff-leave-new-btn"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1" /> New Leave Request
                        </Button>
                    )}
                </div>
                <div className="asl-table-scroll">
                    <table className="asl-table asl-table--zebra" data-testid="staff-leave-balances-table">
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold asl-navy-text"
                                    data-testid="staff-leave-th-type"
                                >
                                    Leave Type
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold asl-navy-text"
                                    data-testid="staff-leave-th-entitled"
                                >
                                    Entitled
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold asl-navy-text"
                                    data-testid="staff-leave-th-used"
                                >
                                    Used
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold asl-navy-text"
                                    data-testid="staff-leave-th-available"
                                >
                                    Available
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {LEAVE_TYPE_ORDER.map((code) => {
                                const b = balancesByCode[code];
                                if (!b) return null;
                                return (
                                    <tr
                                        key={code}
                                        data-testid={`staff-leave-row-${code}`}
                                    >
                                        <td className="px-3 py-2 font-semibold asl-navy-text text-sm">
                                            {LEAVE_TYPE_LABEL[code]}
                                        </td>
                                        <EntitledCell
                                            value={b.entitled_days}
                                            leaveTypeLabel={LEAVE_TYPE_LABEL[code]}
                                            employeeName={employee.full_name}
                                            testid={`staff-leave-entitled-${code}`}
                                        />
                                        <UsedCell
                                            value={b.used_days}
                                            entitled={b.entitled_days || 0}
                                            leaveTypeLabel={LEAVE_TYPE_LABEL[code]}
                                            employeeName={employee.full_name}
                                            testid={`staff-leave-used-${code}`}
                                        />
                                        <td
                                            className="px-3 py-2 text-right text-sm tabular-nums font-medium"
                                            data-testid={`staff-leave-available-${code}`}
                                        >
                                            {b.entitled_days_per_year === null || b.entitled_days_per_year === undefined
                                                ? "—"
                                                : Number(b.available_days || 0).toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* -------- Annual accrual detail -------- */}
            {annual?.accrual && (
                <div className="asl-card p-5" data-testid="staff-leave-annual-accrual">
                    <SectionHeader testid="staff-leave-annual-accrual-title">
                        Annual Leave — Accrual cycle
                    </SectionHeader>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Fact
                            label="Cycle"
                            value={`${annual.accrual.fiscal_year_start} → ${annual.accrual.fiscal_year_end}`}
                            testid="staff-leave-annual-cycle"
                        />
                        <Fact
                            label="Accrued so far"
                            value={`${annual.accrual.accrued} day(s)`}
                            testid="staff-leave-annual-accrued"
                        />
                        <Fact
                            label="Used"
                            value={`${annual.accrual.used} day(s)`}
                            testid="staff-leave-annual-used"
                        />
                        <Fact
                            label="Carried over"
                            value={`${annual.accrual.carried_over} day(s)`}
                            testid="staff-leave-annual-carried"
                        />
                        <Fact
                            label="Remaining"
                            value={`${annual.accrual.remaining} day(s)`}
                            testid="staff-leave-annual-remaining"
                        />
                        <Fact
                            label="Next reset"
                            value={annual.accrual.next_reset}
                            testid="staff-leave-annual-next-reset"
                        />
                        <Fact
                            label="Policy"
                            value={annual.accrual.carry_over_policy}
                            testid="staff-leave-annual-policy"
                        />
                        <Fact
                            label="Configured cap"
                            value={`${annual.entitled_days} day(s)/yr`}
                            testid="staff-leave-annual-cap"
                        />
                    </div>
                </div>
            )}

            {/* -------- Sick tier status -------- */}
            {sick?.sick && (
                <div className="asl-card p-5" data-testid="staff-leave-sick-tier">
                    <SectionHeader testid="staff-leave-sick-title">
                        Sick Leave — Article 70 tier
                    </SectionHeader>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Fact
                            label="Cumulative used"
                            value={`${sick.sick.cumulative_used} day(s)`}
                            testid="staff-leave-sick-cumulative"
                        />
                        <Fact
                            label="Current tier"
                            value={sick.sick.current_tier?.label || "—"}
                            testid="staff-leave-sick-tier-label"
                        />
                        <Fact
                            label="Cycle start"
                            value={sick.sick.cycle_start}
                            testid="staff-leave-sick-cycle-start"
                        />
                        <Fact
                            label="Cycle end"
                            value={sick.sick.cycle_end}
                            testid="staff-leave-sick-cycle-end"
                        />
                    </div>
                </div>
            )}

            {/* -------- Breastfeeding period (female only) -------- */}
            {isFemale && brfPeriod?.start_date && (
                <div className="asl-card p-5" data-testid="staff-leave-brf-period">
                    <SectionHeader testid="staff-leave-brf-title">
                        Breastfeeding — Article 130 (1 hr/day paid)
                    </SectionHeader>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Fact
                            label="Status"
                            value={brfActive ? "Active" : "Expired"}
                            testid="staff-leave-brf-status"
                        />
                        <Fact
                            label="Start date"
                            value={formatDate(brfPeriod.start_date)}
                            testid="staff-leave-brf-start"
                        />
                        <Fact
                            label="Expires on"
                            value={brfPeriod.expires_on ? formatDate(brfPeriod.expires_on) : "—"}
                            testid="staff-leave-brf-expires"
                        />
                        {brfPeriod.child_dob && (
                            <Fact
                                label="Child DOB"
                                value={formatDate(brfPeriod.child_dob)}
                                testid="staff-leave-brf-child-dob"
                            />
                        )}
                    </div>
                </div>
            )}

            {/* -------- Request history -------- */}
            <div className="asl-card p-5">
                <SectionHeader testid="staff-leave-history-title">
                    Request history ({requests.length})
                </SectionHeader>
                {requests.length === 0 ? (
                    <EmptyState
                        icon={Calendar}
                        title="No leave requests yet"
                        description="Requests created for this employee will appear here."
                        testid="staff-leave-history-empty"
                    />
                ) : (
                    <div className="asl-table-scroll">
                        <table
                            className="asl-table asl-table--zebra"
                            data-testid="staff-leave-history-table"
                        >
                            <thead>
                                <tr>
                                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold asl-navy-text">Date range</th>
                                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold asl-navy-text">Type</th>
                                    <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold asl-navy-text">Days</th>
                                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold asl-navy-text">Status</th>
                                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold asl-navy-text">Reason</th>
                                    <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider font-bold asl-navy-text">Files</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((r) => (
                                    <tr
                                        key={r.id}
                                        data-testid={`staff-leave-history-row-${r.id}`}
                                    >
                                        <td className="px-3 py-2 text-sm mono">
                                            {r.start_date} → {r.end_date}
                                        </td>
                                        <td className="px-3 py-2 text-sm font-medium">
                                            {r.leave_type_name || r.leave_type_id_name || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right tabular-nums">
                                            {r.total_days ?? "—"}
                                        </td>
                                        <td className="px-3 py-2 text-sm">
                                            <StatusPill status={r.status} />
                                        </td>
                                        <td
                                            className="px-3 py-2 text-sm text-[color:var(--asl-muted)] max-w-xs truncate"
                                            title={r.reason || ""}
                                        >
                                            {r.reason || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            {(r.attachments || []).length > 0 ? (
                                                <span
                                                    className="inline-flex items-center gap-1 text-xs asl-navy-text font-medium"
                                                    data-testid={`staff-leave-attachment-count-${r.id}`}
                                                    title={(r.attachments || []).map((a) => a.filename).join(", ")}
                                                >
                                                    <Paperclip className="h-3 w-3" />
                                                    {(r.attachments || []).length}
                                                </span>
                                            ) : (
                                                <span className="text-[color:var(--asl-text-3)] text-xs">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function Fact({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                {label}
            </div>
            <div className="text-sm font-semibold asl-navy-text mt-0.5">
                {value ?? "—"}
            </div>
        </div>
    );
}
